import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { userAPI } from '../../../services';

const REQUIRED_COLUMNS = ['First Name'];
const COLUMN_MAP = {
  'First Name':       'firstName',
  'Last Name':        'lastName',
  'Employee ID':      'employeeId',
  'Email':            'email',
  'Phone':            'phone',
  'Gender':           'gender',
  'Department':       'department',
  'Designation':      'designation',
  'Date of Joining':  'dateOfJoining',
  'Employee Type':    'employeeType',
  'Role':             'role',
  'Location Type':    'locationType',
  'Shift':            'shift',
  'Reporting To':     'reportingTo',
  'Probation Days':   'probationDays',
  'CTC':              'ctc',
  'Basic Salary':     'basicSalary',
  'HRA':              'hra',
  'Allowances':       'allowances',
  'PF':               'pf',
  'ESI':              'esi',
  'Professional Tax': 'professionalTax',
  'TDS':              'tds',
};

const EmployeeBulkUpload = ({ onUploadSuccess, onCancel }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    uploadedCount: 0,
    failedCount: 0,
    errors: []
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selectedFile);
    setError('');
    setSuccessMessage('');
  };

  const parseExcelFile = () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }
    setLoading(true);


    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setError('The Excel file is empty');
          setLoading(false);
          return;
        }

        // Validate required columns
        const firstRow = jsonData[0];
        const missingColumns = REQUIRED_COLUMNS.filter(col => !(col in firstRow));
        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}`);
          setLoading(false);
          return;
        }

        // Transform rows
        const transformedData = jsonData.map((row, index) => {
          const emp = { rowNumber: index + 1 };
          Object.entries(COLUMN_MAP).forEach(([colName, fieldName]) => {
            if (row[colName] !== undefined && row[colName] !== '') {
              emp[fieldName] = row[colName]?.toString().trim();
            }
          });
          emp.name = [emp.firstName, emp.lastName].filter(Boolean).join(' ');
          return emp;
        });

        // Basic validation
        const validationErrors = [];
        transformedData.forEach(row => {
          if (!row.firstName) {
            validationErrors.push(`Row ${row.rowNumber}: First Name is required`);
          }
        });

        if (validationErrors.length > 0) {
          setError(`Validation errors:\n${validationErrors.slice(0, 10).join('\n')}${validationErrors.length > 10 ? `\n... and ${validationErrors.length - 10} more` : ''}`);
          setLoading(false);
          return;
        }

        setPreviewData(transformedData);
        setIsPreviewMode(true);
        setLoading(false);
      } catch (err) {
        setError('Error parsing Excel file. Please check the file format.');
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleConfirmUpload = async () => {
    setLoading(true);
    setError('');
    setUploadProgress({ isUploading: true, uploadedCount: 0, failedCount: 0, errors: [] });

    try {
      const result = await userAPI.bulkUploadEmployees(previewData);
      const data = result.data || {};
      const uploadedCount = data.uploadedCount || 0;
      const failedCount = data.failedCount || 0;
      const errors = data.errors || [];

      setUploadProgress({ isUploading: false, uploadedCount, failedCount, errors });

      let message = `Successfully created ${uploadedCount} employee${uploadedCount !== 1 ? 's' : ''}`;
      if (failedCount > 0) message += ` (${failedCount} failed)`;
      setSuccessMessage(message);

    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setUploadProgress(prev => ({ ...prev, isUploading: false }));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await userAPI.downloadBulkUploadTemplate();
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewData([]);
    setIsPreviewMode(false);
    setError('');
    setSuccessMessage('');
    setUploadProgress({ isUploading: false, uploadedCount: 0, failedCount: 0, errors: [] });
    const fileInput = document.getElementById('employee-bulk-file-input');
    if (fileInput) fileInput.value = '';
  };

  // Success screen
  if (successMessage && !uploadProgress.isUploading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="text-center mb-4">
            <div className="mb-4 text-green-600">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Complete!</h3>
            <p className="text-gray-600 mb-2">{successMessage}</p>
          </div>
          {uploadProgress.errors && uploadProgress.errors.length > 0 && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm font-medium text-yellow-800 mb-2">Errors ({uploadProgress.errors.length}):</p>
              <ul className="text-xs text-yellow-700 space-y-1 max-h-32 overflow-y-auto">
                {uploadProgress.errors.slice(0, 20).map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
                {uploadProgress.errors.length > 20 && (
                  <li>... and {uploadProgress.errors.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
          <button
            onClick={() => { onUploadSuccess && onUploadSuccess(); }}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Progress screen
  if (uploadProgress.isUploading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Uploading Employees...</h3>
          <p className="text-sm text-gray-500">Please don't close this window</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {isPreviewMode ? `Preview Employee Data (${previewData.length} rows)` : 'Bulk Import Employees'}
          </h2>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!isPreviewMode ? (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Download the template Excel file — it contains sample data and an Instructions sheet</li>
                <li>• <strong>Required:</strong> First Name</li>
                <li>• <strong>Optional:</strong> Last Name, Email, Phone, Department, Designation, Role, Date of Joining, Gender, Employee Type, Shift, Reporting To, Salary fields</li>
                <li>• Employee ID is auto-generated if left empty</li>
                <li>• Login credentials will be emailed to employees if an email is provided</li>
                <li>• Upload only .xlsx or .xls files</li>
              </ul>
            </div>

            {/* Download Template */}
            <div className="flex justify-center">
              <button
                onClick={handleDownloadTemplate}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Template
              </button>
            </div>

            {/* File Upload Drop Zone */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <input
                id="employee-bulk-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <label htmlFor="employee-bulk-file-input" className="cursor-pointer">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium text-gray-600">Click to select Excel file</p>
                <p className="text-sm text-gray-400 mt-1">Supports .xlsx and .xls</p>
              </label>
              {file && (
                <div className="mt-4 inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-green-700 font-medium">{file.name}</span>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={parseExcelFile}
                disabled={!file || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading && (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                Preview Data
              </button>
            </div>
          </div>
        ) : (
          /* Preview Mode */
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-800 text-sm font-medium">
                {previewData.length} employee{previewData.length !== 1 ? 's' : ''} ready to import. Review the data below before confirming.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* Preview Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dept</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DoJ</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Emp ID</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {previewData.map((emp, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-gray-500">{emp.rowNumber}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{emp.name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{emp.email || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{emp.phone || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{emp.department || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{emp.designation || '—'}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {emp.role || 'employee'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{emp.dateOfJoining || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{emp.employeeType || 'permanent'}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{emp.employeeId || 'auto'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {previewData.length > 10 && (
              <p className="text-xs text-gray-500 text-center">
                Showing all {previewData.length} rows. Scroll to review.
              </p>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center pt-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpload}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {loading && (
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  Import {previewData.length} Employee{previewData.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeBulkUpload;
